import { useCallback, useEffect, useRef, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowBack,
  IconFacebook,
  IconInstagram,
  IconTiktok,
  IconX,
  IconYoutube,
} from "../components/CustomIcons";
import Cropper from "react-easy-crop";
import { cropImageFile } from "../components/utils/cropImage";
import { validateImageFile } from "../components/utils/imageValidation";
import { resizeImage } from "../components/utils/resizeImage";
import MarkDownWithOption from "../components/ui/MarkDownWithOption";
import {
  getLeagueBySlug,
  Team,
  updateLeagueProgressStep,
} from "../components/leagueLocker/firestoreQuery";
import { useAuth } from "../contexts/AuthContext";
import {
  formatSocialLink,
  SocialPlatform,
} from "../components/utils/formatSocialLink";
import { uploadImage } from "../services/firebase";
import { updateCommissionerTeamDetails } from "../components/leagueLocker/firestoreQuery";
import squareIcon from "../assets/images/icon-square.png";
import venmoIcon from "../assets/images/icon-venmo.png";
import PayPalIcon from "../assets/images/icon-PayPal.png";
import { SectionLoader } from "../components/ui/SectionLoader";

const EditMyTeamPage = () => {
  const { leagueSlug } = useParams<{ leagueSlug?: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showCropper, setShowCropper] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<any>(null);
  const [loadingCropper, setLoadingCropper] = useState(false);
  const [leagueId, setLeagueId] = useState<string | null>(null); //Edit mode
  const [teamImage, setTeamImage] = useState<File | null>(null);
  const [teamName, setTeamName] = useState("");
  const [teams, setTeams] = useState<Team[]>([]);
  const [leagueCreatedBy, setLeagueCreatedBy] = useState<string | null>(null);
  const [errors, setErrors] = useState<{
    teamName?: string;
  }>({});
  const [teamBio, setTeamBio] = useState<string>("");
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [teamHometown, setTeamHometown] = useState<string | null>(null);
  const [tiktokValue, setTiktokValue] = useState("");
  const [youTubeValue, setYouTubeValue] = useState("");
  const [facebookValue, setFacebookValue] = useState("");
  const [instagramValue, setInstagramValue] = useState("");
  const [xValue, setxValue] = useState("");
  const [socialErrors, setSocialErrors] = useState<{
    tiktok?: string;
    youtube?: string;
    facebook?: string;
    instagram?: string;
    x?: string;
  }>({});
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [isSubmit, setIsSubmit] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [existingTeamImage, setExistingTeamImage] = useState<string | null>(
    null
  );
  const teamImageRef = useRef<HTMLInputElement | null>(null);
  const [venmoValue, setVenmoValue] = useState("");
  const [cashAppValue, setCashAppValue] = useState("");
  const [payPalValue, setPayPalValue] = useState("");

  useEffect(() => {
    const fetchLeagueDetails = async () => {
      if (leagueSlug) {
        try {
          setIsLoading(true);
          const res = await getLeagueBySlug(leagueSlug);
          if (res) {
            // find the team associated with the user
            const userTeam = res.teams?.find(
              (team: any) => team.createdBy === user?.uid
            );
            setLeagueCreatedBy(res.createdBy || null);
            setLeagueId(res.id || null);
            setTeamName(userTeam?.name || "");
            setTeamBio(userTeam?.description || "");
            setTeams(res.teams || []);
            setTeamHometown(userTeam?.homeTown || "");
            setImagePreview(userTeam?.imageUrl || null);
            setExistingTeamImage(userTeam?.imageUrl || null);
            setTiktokValue(userTeam?.tiktok || "");
            setVenmoValue(userTeam?.venmo || "");
            setCashAppValue(userTeam?.cashApp || "");
            setPayPalValue(userTeam?.payPal || "");
            setYouTubeValue(userTeam?.youtube || "");
            setFacebookValue(userTeam?.facebook || "");
            setInstagramValue(userTeam?.instagram || "");
            setxValue(userTeam?.x || "");
          }
        } catch (error) {
          console.error("Error fetching league:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchLeagueDetails();
  }, [leagueSlug]);

  const isFormValid =
    teamName.trim() !== "" && Object.values(errors).every((err) => !err);

  const onCropComplete = useCallback((_: any, croppedPixels: any) => {
    setCroppedArea(croppedPixels);
  }, []);

  const handleCropConfirm = useCallback(async () => {
    if (!tempImageUrl || !croppedArea) return;
    const cropped = await cropImageFile(tempImageUrl, croppedArea);
    if (cropped) {
      setTeamImage(cropped);
      setImagePreview(URL.createObjectURL(cropped));
    }
    setShowCropper(false);
  }, [tempImageUrl, croppedArea]);

  const handleImageChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const validationError = validateImageFile(file);
      if (validationError) {
        alert(`${validationError}`);
        return;
      }
      setLoadingCropper(true);
      try {
        const { resizedUrl } = await resizeImage(file);
        setTempImageUrl(resizedUrl);
        setShowCropper(true);
      } finally {
        setLoadingCropper(false);
      }
    },
    []
  );

  const handleRemoveImage = () => {
    setTeamImage(null);
    setImagePreview(null);
    if (teamImageRef.current) teamImageRef.current.value = "";
  };

  const handleTeamNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTeamName(value);
    setErrors((prev) => ({ ...prev, teamName: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmit(true);

    // Validate if the team name exists in the league
    const teamWithoutCurrentUser = teams.filter(
      (team: any) => team.createdBy !== user?.uid
    );
    const teamNameExists = teamWithoutCurrentUser.some(
      (team: any) => team.name === teamName.trim()
    );
    if (teamNameExists) {
      setErrors({ teamName: "Team Name already exists in the league" });
      setIsSubmit(false);
      return;
    }

    // Validate Team Name
    if (!teamName.trim()) {
      setErrors({ teamName: "Team Name is required" });
      setIsSubmit(false);
      return;
    } else {
      setErrors({});
    }

    // Social platform display names for error messages
    const socialDisplayNames: Record<string, string> = {
      tiktok: "TikTok",
      youtube: "YouTube",
      facebook: "Facebook",
      instagram: "Instagram",
      x: "X",
    };

    // Format and validate social links
    const socialFields = [
      { key: "tiktok", value: tiktokValue },
      { key: "youtube", value: youTubeValue },
      { key: "facebook", value: facebookValue },
      { key: "instagram", value: instagramValue },
      { key: "x", value: xValue },
    ];

    const formattedSocial: Record<string, string | null> = {};
    const newSocialErrors: typeof socialErrors = {};

    socialFields.forEach(({ key, value }) => {
      if (value) {
        const formatted = formatSocialLink(key as SocialPlatform, value);
        formattedSocial[key] = formatted;
        if (!formatted) {
          newSocialErrors[
            key as keyof typeof socialErrors
          ] = `Please enter a valid ${socialDisplayNames[key]} URL or handle`;
        }
      } else {
        formattedSocial[key] = null;
      }
    });

    if (Object.keys(newSocialErrors).length > 0) {
      setSocialErrors(newSocialErrors);
      setIsSubmit(false);
      return;
    } else {
      setSocialErrors({});
    }

    // Validate at least one payment method is there:
    if (
      venmoValue.trim() === "" &&
      cashAppValue.trim() === "" &&
      payPalValue.trim() === ""
    ) {
      setPaymentError("Please select at least one payment handler.");
      setIsSubmit(false);
      return;
    } else {
      setPaymentError(null);
    }

    // Handle team image upload
    let teamImageUrl = imagePreview ? existingTeamImage ?? "" : "";
    if (teamImage) {
      try {
        teamImageUrl = await uploadImage(teamImage);
      } catch (error) {
        console.error("Error uploading team image:", error);
      }
    }

    const finalPayload = {
      name: teamName.trim(),
      description: teamBio.trim(),
      homeTown: teamHometown?.trim() || null,
      imageUrl: imagePreview ? teamImageUrl : null,
      ...formattedSocial,
      venmo: venmoValue.trim() || null,
      cashApp: cashAppValue.trim() || null,
      payPal: payPalValue.trim() || null,
    };

    if (!leagueId || !user?.uid) {
      console.error("Missing leagueId or user ID");
      setIsSubmit(false);
      return;
    }

    try {
      setIsLoading(true);
      await updateCommissionerTeamDetails(leagueId, user.uid, finalPayload);
      if (leagueCreatedBy === user?.uid) {
        await updateLeagueProgressStep(leagueId, "edit-your-team");
      }
      const fromTeamsPage = window.history.state?.usr?.fromTeamsPage;
      if (fromTeamsPage) {
        navigate(`/${leagueSlug}/teams`);
      } else {
        if (leagueCreatedBy === user?.uid) {
          navigate(`/manage-league/${leagueSlug}`);
        } else {
          navigate(`/${leagueSlug}/teams`);
        }
      }
    } catch (error) {
      console.error("Error updating team:", error);
    } finally {
      setIsSubmit(false);
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <SectionLoader />;
  }

  return (
    <div className="pageOuterWrapper">
      <Navbar />
      <div className="contentArea otherContentArea">
        <div className="wrapper">
          {leagueCreatedBy === user?.uid && (
            <div className="backWrap">
              <Link to={`/manage-league/${leagueSlug}`}>
                <ArrowBack /> Back
              </Link>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="yourLeagueHeader">
              <div className="headingItem">Team Details</div>
              <div className="actionItems">
                <button
                  type="submit"
                  className="btnStyle btnFilled"
                  disabled={!isFormValid || isSubmit}
                >
                  SAVE
                </button>
              </div>
            </div>
            {showCropper && tempImageUrl && (
              <div className="popupWrapper fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
                <div className="relative w-[90vw] max-w-2xl h-[90vh] bg-white rounded-lg overflow-hidden shadow-lg">
                  <Cropper
                    image={tempImageUrl}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                  />
                  <div className="absolute bottom-0 left-0 w-full flex justify-between p-4 bg-white border-t">
                    <button
                      type="button"
                      onClick={() => setShowCropper(false)}
                      className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleCropConfirm}
                      className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                    >
                      Crop & Save
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="shadowBlk">
              <div className="blkHeading">📸 Team Photo</div>
              <div className="uploadArea">
                {!imagePreview && (
                  <>
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      ref={teamImageRef}
                      onChange={handleImageChange}
                    />
                    <button
                      className="btnStyle btnHighlighted"
                      type="button"
                      onClick={() => teamImageRef.current?.click()}
                    >
                      UPLOAD TEAM PHOTO
                    </button>
                  </>
                )}

                {imagePreview && (
                  <div className="relative inline-block">
                    <img
                      src={imagePreview}
                      alt="Punishment Preview"
                      style={{
                        width: 200,
                        borderRadius: 8,
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        handleRemoveImage();
                      }}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                      style={{ lineHeight: 1, fontSize: 16 }}
                      aria-label="Remove image"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="shadowBlk">
              <div className="blkHeading">✏️ Edit Your Team Settings</div>
              <div className="formGroup">
                <label htmlFor="teamName">
                  Team Name <span className="reqTxt">Required</span>
                </label>
                <input
                  type="text"
                  id="teamName"
                  placeholder="Enter your team name"
                  value={teamName}
                  onChange={handleTeamNameChange}
                  maxLength={100}
                />
                {errors.teamName && (
                  <div className="errorText">{errors.teamName}</div>
                )}
              </div>
              <div className="formGroup">
                <label htmlFor="leagueName">Team Bio</label>
                <MarkDownWithOption
                  value={teamBio}
                  onChange={(value) => {
                    setTeamBio(value ?? "");
                  }}
                />
              </div>
              <div className="formGroup">
                <label htmlFor="teamHometown">Team Hometown</label>
                <input
                  type="text"
                  id="teamHometown"
                  placeholder="Enter your team hometown"
                  value={teamHometown ?? ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setTeamHometown(val);
                  }}
                />
              </div>
            </div>
            <div className="shadowBlk">
              <div className="blkHeading">😎 Your Social Media</div>
              <div className="socialEditWrap">
                <div className="formGroup">
                  <div className="fldHavePrefix">
                    <span className="fldPrefix">
                      <IconTiktok />
                    </span>
                    <input
                      type="text"
                      placeholder="johndoe"
                      className="fldStyle"
                      value={tiktokValue}
                      onChange={(e) => {
                        setTiktokValue(e.target.value);
                        if (socialErrors.tiktok) {
                          // clear the facebook error as soon as the user types
                          setSocialErrors((prev) => ({ ...prev, tiktok: "" }));
                        }
                      }}
                    />
                  </div>
                  {socialErrors.tiktok && (
                    <span className="errorText">{socialErrors.tiktok}</span>
                  )}
                </div>
                <div className="formGroup">
                  <div className="fldHavePrefix">
                    <span className="fldPrefix">
                      <IconYoutube />
                    </span>
                    <input
                      type="text"
                      placeholder="johndoe"
                      className="fldStyle"
                      value={youTubeValue}
                      onChange={(e) => {
                        setYouTubeValue(e.target.value);
                        if (socialErrors.youtube) {
                          // clear the facebook error as soon as the user types
                          setSocialErrors((prev) => ({ ...prev, youtube: "" }));
                        }
                      }}
                    />
                  </div>
                  {socialErrors.youtube && (
                    <span className="errorText">{socialErrors.youtube}</span>
                  )}
                </div>
                <div className="formGroup">
                  <div className="fldHavePrefix">
                    <span className="fldPrefix">
                      <IconFacebook />
                    </span>
                    <input
                      type="text"
                      placeholder="johndoe"
                      className="fldStyle"
                      value={facebookValue}
                      onChange={(e) => {
                        setFacebookValue(e.target.value);
                        if (socialErrors.facebook) {
                          // clear the facebook error as soon as the user types
                          setSocialErrors((prev) => ({
                            ...prev,
                            facebook: "",
                          }));
                        }
                      }}
                    />
                  </div>
                  {socialErrors.facebook && (
                    <span className="errorText">{socialErrors.facebook}</span>
                  )}
                </div>
                <div className="formGroup">
                  <div className="fldHavePrefix">
                    <span className="fldPrefix">
                      <IconInstagram />
                    </span>
                    <input
                      type="text"
                      placeholder="johndoe"
                      className="fldStyle"
                      value={instagramValue}
                      onChange={(e) => {
                        setInstagramValue(e.target.value);
                        if (socialErrors.instagram) {
                          // clear the facebook error as soon as the user types
                          setSocialErrors((prev) => ({
                            ...prev,
                            instagram: "",
                          }));
                        }
                      }}
                    />
                  </div>
                  {socialErrors.instagram && (
                    <span className="errorText">{socialErrors.instagram}</span>
                  )}
                </div>
                <div className="formGroup">
                  <div className="fldHavePrefix">
                    <span className="fldPrefix">
                      <IconX />
                    </span>
                    <input
                      type="text"
                      placeholder="johndoe"
                      className="fldStyle"
                      value={xValue}
                      onChange={(e) => {
                        setxValue(e.target.value);
                        if (socialErrors.x) {
                          // clear the facebook error as soon as the user types
                          setSocialErrors((prev) => ({ ...prev, x: "" }));
                        }
                      }}
                    />
                  </div>
                  {socialErrors.x && (
                    <span className="errorText">{socialErrors.x}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="shadowBlk">
              <div className="blkHeading">🤑 Get Paid</div>
              <div className="socialEditWrap">
                <div className="formGroup">
                  <div className="fldHavePrefix">
                    <span className="fldPrefix">
                      <img src={venmoIcon} alt="Venmo Icon" />
                    </span>
                    <input
                      type="text"
                      placeholder="johndoe"
                      className="fldStyle"
                      value={venmoValue}
                      onChange={(e) => {
                        setVenmoValue(e.target.value);
                        if (paymentError) {
                          // clear the venmo error as soon as the user types
                          setPaymentError(null);
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="formGroup">
                  <div className="fldHavePrefix">
                    <span className="fldPrefix">
                      <img src={squareIcon} alt="Cash App Icon" />
                    </span>
                    <input
                      type="text"
                      placeholder="johndoe"
                      className="fldStyle"
                      value={cashAppValue}
                      onChange={(e) => {
                        setCashAppValue(e.target.value);
                        if (paymentError) {
                          // clear the cashApp error as soon as the user types
                          setPaymentError(null);
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="formGroup">
                  <div className="fldHavePrefix">
                    <span className="fldPrefix">
                      <img src={PayPalIcon} alt="PayPal Icon" />
                    </span>
                    <input
                      type="text"
                      placeholder="johndoe"
                      className="fldStyle"
                      value={payPalValue}
                      onChange={(e) => {
                        setPayPalValue(e.target.value);
                        if (paymentError) {
                          // clear the paypal error as soon as the user types
                          setPaymentError(null);
                        }
                      }}
                    />
                  </div>
                  {paymentError && (
                    <span className="errorText">{paymentError}</span>
                  )}
                </div>
              </div>
            </div>
            <button
              type="submit"
              className="btnStyle btnFilled btnFull"
              disabled={!isFormValid || isSubmit}
            >
              SAVE
            </button>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default EditMyTeamPage;
// ...existing code...
